import { CurrentSchoolYear } from 'Src/ng2/shared/constants/current-school-year.constant';
import { IStudent } from 'Src/ng2/shared/typings/interfaces/student.interface';
import { ImStudent } from 'Src/ng2/shared/services/im-models/im-student.service';
import { ISchool } from 'Src/ng2/shared/typings/interfaces/school.interface';
import { IStateParamsService, IStateService } from 'angular-ui-router';
import * as _ from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { take, takeUntil, tap } from 'rxjs/operators';
import { PATH_TYPE } from '../../../ng2/shared/services/api-service/api-service';
import { LocalStorageService } from '../../../ng2/shared/services/web-storage/local-storage/local-storage.service';
import { IStudentSupport } from '../../../ng2/shared/typings/interfaces/student-support.interface';
import {
  getCollegePathLoadedStatus,
  getStudentAssessmentsEntitiesList,
  getStudentAssessmentsLoadedStatus,
  LoadCollegePath,
  LoadStudentAssessments,
} from '../../../ng2/store';
import { LoadDocLogs } from '../../../ng2/store/actions/doc-logs-actions';
import { LoadFlags } from '../../../ng2/store/actions/flag-actions';
import { LoadStudentMilestones } from '../../../ng2/store/actions/milestone-actions';
import {
  getCareerPathEntitiesList,
  getCareerPathLoadedStatus,
} from '../../../ng2/store/selectors/career-path-selectors';
import { getDocLogsEntitiesList } from '../../../ng2/store/selectors/doc-logs-selectors';
import { getFlagEntitiesList, getFlagsLoadedStatus } from '../../../ng2/store/selectors/flags-selectors';
import { getMilestoneEntitiesList, getMilestoneLoadedEntities } from '../../../ng2/store/selectors/milestone-selectors';
import StudentPanelsConfig from '../../../ng2/student/student-container/student-panels.config';
import { ISdcStore } from '../../sdc/flux/sdc-store.interface';
import { LoadActivities } from './../../../ng2/store/actions/activities-actions';
import { LoadCareerPath } from './../../../ng2/store/actions/career-path-actions';
import { LoadPartnerOrgs } from './../../../ng2/store/actions/partner-orgs-actions';
import { LoadStudentActivities } from './../../../ng2/store/actions/student-activities-actions';
import { LoadStudentPath } from './../../../ng2/store/actions/student-paths-actions';
import { LoadStudentSupports } from './../../../ng2/store/actions/student-supports-actions';
import {
  getActivitiesEntitiesList,
  getActivitiesLoadedStatus,
} from './../../../ng2/store/selectors/activities-selectors';
import { getCollegePathEntities } from './../../../ng2/store/selectors/college-path-selectors';
import { getDocLogsEntities } from './../../../ng2/store/selectors/doc-logs-selectors';
import {
  getPartnerOrgsEntitiesList,
  getPartnerOrgsLoadedStatus,
} from './../../../ng2/store/selectors/partner-orgs-selectors';
import {
  getStudentActivitiesEntitiesList,
  getStudentActivitiesLoadedStatus,
} from './../../../ng2/store/selectors/student-activities-selectors';
import {
  getAllStudentPathsLoadedStatus,
  getStudentPathsEntities,
  getStudentPathsLoadedEntities,
} from './../../../ng2/store/selectors/student-paths-selector';
import {
  getStudentSupportsEntities,
  getStudentSupportsEntitiesList,
  getStudentSupportsLoadedStatus,
} from './../../../ng2/store/selectors/student-supports-selectors';

/* istanbul ignore next */
angular.module('nv.routes.main').config(function($stateProvider) {
  // TODO: should this go on its own service?
  let cachedSchoolUsers = null;
  let cachedStudents = null;
  let cachedFilteredStudents = null;

  function canUserViewObjPartial(viewingUser) {
    return function(obj) {
      return obj.canView(viewingUser);
    };
  }

  const schoolUsersResolve = [
    'User',
    'currentSchool',
    'currentUser',
    function(User, currentSchool, currentUser) {
      // only call users endpoint if schoolUsers have not been cached
      if (cachedSchoolUsers === null) {
        let promise = User.fetchUsersWithFullAccess({
          schoolId: currentSchool._id,
          projection: 'MINI',
        }).$promise;

        // TODO: Should returning undefined if a promise fails be a pattern?
        // Otherwise, resolve block fails and state does not load.
        return promise.then(
          users => {
            const canUserViewObj = canUserViewObjPartial(currentUser);
            const viewableUsers = _.filter(users, canUserViewObj);

            return (cachedSchoolUsers = viewableUsers);
          },
          function() {
            // TODO: Handle failure case?
            return (cachedSchoolUsers = undefined); // allows for state transition to take place if api returns error
          },
        );
      }

      return cachedSchoolUsers;
    },
  ];

  const flagsResolve$ = [
    'Store',
    'currentSchool',
    'currentStudentId',
    (Store, currentSchool, currentStudentId) => {
      const { _id } = currentSchool;
      const payload = { schoolId: _id };
      const flagsLoaded = getFlagsLoadedStatus;
      const destroy$: Subject<boolean> = new Subject<boolean>();

      return new Promise(resolve => {
        Store.select(flagsLoaded)
          .pipe(takeUntil(destroy$))
          .subscribe(loaded => {
            if (!loaded) {
              Store.dispatch(new LoadFlags(payload));
            } else {
              const flags = getFlagEntitiesList;
              const flags$ = Store.select(flags);
              destroy$.next(true);
              destroy$.unsubscribe();
              resolve(flags$);
            }
          });
      });
    },
  ];

  const studentAssessmentsResolve$ = [
    'Store',
    'currentSchool',
    (Store, currentSchool) => {
      const { _id } = currentSchool;
      const payload = { schoolId: _id, schoolYear: CurrentSchoolYear.WITH_SY_PREFIX };
      const studentAssessmentsLoaded = getStudentAssessmentsLoadedStatus;
      const destroy$: Subject<boolean> = new Subject<boolean>();

      return new Promise(resolve => {
        Store.select(studentAssessmentsLoaded)
          .pipe(takeUntil(destroy$))
          .subscribe(loaded => {
            if (!loaded) {
              Store.dispatch(new LoadStudentAssessments(payload));
            } else {
              const studentAssessments = getStudentAssessmentsEntitiesList;
              const studentAssessments$ = Store.select(studentAssessments);
              destroy$.next(true);
              destroy$.unsubscribe();
              resolve(studentAssessments$);
            }
          });
      });
    },
  ];

  const studentPathsResolve = [
    'Store',
    'currentSchool',
    'currentStudentId',
    (Store, currentSchool, currentStudentId) => {
      const payload = { schoolId: currentSchool._id, studentId: currentStudentId };
      const destroy$: Subject<boolean> = new Subject<boolean>();
      const loadedStudentPathEntities$: Observable<any> = Store.select(getStudentPathsLoadedEntities);
      const studentPathAllLoadedStatus$: Observable<any> = Store.select(getAllStudentPathsLoadedStatus);
      const studentPathEntities$: Observable<any> = Store.select(getStudentPathsEntities);
      return new Promise(resolve => {
        combineLatest(studentPathAllLoadedStatus$, loadedStudentPathEntities$)
          .pipe(
            tap(([isAllLoaded, loadedStudentPaths]) => {
              if (isAllLoaded || loadedStudentPaths[currentStudentId]) {
                destroy$.next(true);
                resolve(studentPathEntities$);
              } else {
                Store.dispatch(new LoadStudentPath(payload));
              }
            }),
            takeUntil(destroy$),
          )
          .subscribe();
      });
    },
  ];

  const collegePathsResolve = [
    'Store',
    'currentSchool',
    (Store, currentSchool) => {
      const payload = { schoolId: currentSchool._id, pathType: PATH_TYPE.COLLEGE };
      const collegePathLoaded = getCollegePathLoadedStatus;
      const destroy$: Subject<boolean> = new Subject<boolean>();

      return new Promise(resolve => {
        Store.select(collegePathLoaded)
          .pipe(takeUntil(destroy$))
          .subscribe(loaded => {
            if (!loaded) {
              Store.dispatch(new LoadCollegePath(payload));
            } else {
              const paths$ = Store.select(getCollegePathEntities);
              destroy$.next(true);
              destroy$.unsubscribe();
              resolve(paths$);
            }
          });
      });
    },
  ];

  const careerPathsResolve = [
    'Store',
    'currentSchool',
    (Store, currentSchool) => {
      const payload = { schoolId: currentSchool._id, pathType: PATH_TYPE.CAREER };
      const careerPathLoaded = getCareerPathLoadedStatus;
      const destroy$: Subject<boolean> = new Subject<boolean>();

      return new Promise(resolve => {
        Store.select(careerPathLoaded)
          .pipe(takeUntil(destroy$))
          .subscribe(loaded => {
            if (!loaded) {
              Store.dispatch(new LoadCareerPath(payload));
            } else {
              const paths$ = Store.select(getCareerPathEntitiesList);
              destroy$.next(true);
              destroy$.unsubscribe();
              resolve(paths$);
            }
          });
      });
    },
  ];

  const milestoneResolve = [
    'Store',
    'currentSchool',
    'currentStudentId',
    (Store, currentSchool, currentStudentId) => {
      const payload = { schoolId: currentSchool._id, studentId: currentStudentId };
      const destroy$: Subject<boolean> = new Subject<boolean>();
      const milestoneLoadedEntities = getMilestoneLoadedEntities;

      return new Promise(resolve => {
        Store.select(milestoneLoadedEntities)
          .pipe(takeUntil(destroy$))
          .subscribe(loadedEntities => {
            if (!loadedEntities[currentStudentId]) {
              Store.dispatch(new LoadStudentMilestones(payload));
            } else {
              const milestones = getMilestoneEntitiesList;
              const milestones$ = Store.select(milestones);
              destroy$.next(true);
              destroy$.unsubscribe();
              resolve(milestones$);
            }
          });
      });
    },
  ];

  const docLogsResolve = [
    'Store',
    'currentStudentId',
    'currentSchool',
    function(Store, currentStudentId, currentSchool) {
      const schoolId = currentSchool._id;
      const payload = {
        studentId: currentStudentId,
        schoolId,
      };
      const docLogsEntities = getDocLogsEntities;
      const destroy$: Subject<boolean> = new Subject<boolean>();

      return new Promise(resolve => {
        Store.select(docLogsEntities)
          .pipe(takeUntil(destroy$))
          .subscribe(entities => {
            if (!entities[currentStudentId]) {
              Store.dispatch(new LoadDocLogs(payload));
            }
            const docLogs = getDocLogsEntitiesList;
            const docLogs$ = Store.select(docLogs);

            destroy$.next(true);
            destroy$.unsubscribe();

            resolve(docLogs$);
          });
      });
    },
  ];

  const studentDiffsResolve = [
    'CourseDiff',
    'currentStudentId',
    'currentSchool',
    'currentUser',
    'ImSchool',
    function(CourseDiff, currentStudentId, currentSchool, currentUser, ImSchool) {
      let promise = CourseDiff.query({
        studentId: currentStudentId,
        where: {
          $and: [{ status: { $ne: 'DELETED' } }, { termYear: { $eq: ImSchool.getCurrentTermYear(currentSchool) } }],
        },
      }).$promise;

      return promise.then(
        function(courseDiffs) {
          const canUserViewObj = canUserViewObjPartial(currentUser);
          const viewableCourseDiffs = _.filter(courseDiffs, canUserViewObj);

          return viewableCourseDiffs;
        },
        function() {
          return undefined; // allows for state transition to take place if api returns error
        },
      );
    },
  ];

  const studentGapPlansResolve = [
    'GapPlan',
    'currentStudentId',
    'currentUser',
    function(GapPlan, currentStudentId, currentUser) {
      let promise = GapPlan.query({
        studentId: currentStudentId,
      }).$promise;

      return promise.then(
        gapPlans => {
          const canUserViewObj = canUserViewObjPartial(currentUser);
          const viewableGapPlans = _.filter(gapPlans, canUserViewObj);

          return viewableGapPlans;
        },
        function() {
          return undefined; // allows for state transition to take place if api returns error
        },
      );
    },
  ];

  const studentSupportsResolve = [
    'Store',
    'currentSchool',
    'Rollbar',
    (Store, currentSchool, Rollbar) => {
      const { _id } = currentSchool;
      const payload = { schoolId: _id };
      const studentSupportsLoaded = getStudentSupportsLoadedStatus;
      const destroy$: Subject<boolean> = new Subject<boolean>();

      return new Promise(resolve => {
        Store.select(studentSupportsLoaded)
          .pipe(takeUntil(destroy$))
          .subscribe(loaded => {
            if (!loaded) {
              Store.dispatch(new LoadStudentSupports(payload));
            } else {
              const studentSupports = getStudentSupportsEntities;
              const studentSupports$ = Store.select(studentSupports);
              destroy$.next(true);
              destroy$.unsubscribe();
              resolve(studentSupports$);
            }
          });
      });
    },
  ];

  const supportsResolve = [
    'Store',
    'Support',
    'currentSchool',
    'currentStudentId',
    'currentUser',
    (Store, Support, currentSchool, currentStudentId, currentUser) => {
      const { _id } = currentSchool;
      const payload = { schoolId: _id };
      const studentSupportsLoaded = getStudentSupportsLoadedStatus;
      const destroy$: Subject<boolean> = new Subject<boolean>();

      return new Promise(resolve => {
        Store.select(studentSupportsLoaded)
          .pipe(takeUntil(destroy$))
          .subscribe(loaded => {
            if (!loaded) {
              Store.dispatch(new LoadStudentSupports(payload));
            } else {
              Store.select(getStudentSupportsEntitiesList)
                // if studentSupport changes are made, additional queries are handled in student supports panel
                .pipe(take(1))
                .subscribe(studentSupports => {
                  const filteredStudentSupports = _.filter(studentSupports, (studentSupport: IStudentSupport) => {
                    return studentSupport.student.studentId === currentStudentId;
                  });

                  const supportIds = _.map(filteredStudentSupports, studentSupport => {
                    return studentSupport.support.supportId;
                  });

                  const promise = Support.query({
                    where: { _id: { $in: supportIds } },
                  }).$promise;

                  promise.then(
                    supports => {
                      const canUserViewObj = canUserViewObjPartial(currentUser);
                      const viewableSupports = _.filter(supports, canUserViewObj);
                      destroy$.next(true);
                      destroy$.unsubscribe();
                      resolve(viewableSupports);
                    },
                    function() {
                      // TODO: Handle failure case?
                      destroy$.next(true);
                      destroy$.unsubscribe();
                      resolve(undefined);
                    },
                  );
                });
            }
          });
      });
    },
  ];

  const studentListResolver = [
    'StudentListResolver',
    'currentStudentId',
    'Store',
    'currentSchool',
    (StudentListResolver, currentStudentId, Store, currentSchool) => {
      return StudentListResolver.resolveStudentForStudentView$(currentStudentId, Store, currentSchool._id);
    },
  ];

  const singleStudentViewCurrentStudentResolver = [
    'StudentListCurrentStudentResolver',
    'currentStudentId',
    'Store',
    'currentSchool',
    (StudentListCurrentStudentResolver, currentStudentId, Store, currentSchool) => {
      return StudentListCurrentStudentResolver.resolveCurrentStudentForStudentView$(
        currentStudentId,
        Store,
        currentSchool._id,
      );
    },
  ];

  const patchProcessingResolver = [
    'ProcessingUpdateResolver',
    'Store',
    'currentSchool',
    (ProcessingUpdateResolver, Store, currentSchool) => {
      return ProcessingUpdateResolver.resolvePatchProcessing$(Store);
    },
  ];

  const activitiesResolve$ = [
    'Store',
    'currentSchool',
    (Store, currentSchool) => {
      const { _id } = currentSchool;
      const payload = { schoolId: _id };
      const activitiesLoaded = getActivitiesLoadedStatus;
      const destroy$: Subject<boolean> = new Subject<boolean>();

      return new Promise(resolve => {
        Store.select(activitiesLoaded)
          .pipe(takeUntil(destroy$))
          .subscribe(loaded => {
            if (!loaded) {
              Store.dispatch(new LoadActivities(payload));
            } else {
              const activities = getActivitiesEntitiesList;
              const activities$ = Store.select(activities);
              destroy$.next(true);
              destroy$.unsubscribe();
              resolve(activities$);
            }
          });
      });
    },
  ];

  const studentActivitiesResolve$ = [
    'Store',
    'currentSchool',
    (Store, currentSchool) => {
      const { _id } = currentSchool;
      const payload = { schoolId: _id };
      const studentActivitiesLoaded = getStudentActivitiesLoadedStatus;
      const destroy$: Subject<boolean> = new Subject<boolean>();

      return new Promise(resolve => {
        Store.select(studentActivitiesLoaded)
          .pipe(takeUntil(destroy$))
          .subscribe(loaded => {
            if (!loaded) {
              Store.dispatch(new LoadStudentActivities(payload));
            } else {
              const studentActivities = getStudentActivitiesEntitiesList;
              const studentActivities$ = Store.select(studentActivities);
              destroy$.next(true);
              destroy$.unsubscribe();
              resolve(studentActivities$);
            }
          });
      });
    },
  ];

  const partnerOrgsResolve$ = [
    'Store',
    'currentSchool',
    (Store, currentSchool) => {
      const { _id } = currentSchool;
      const payload = { schoolId: _id };
      const partnerOrgsLoaded = getPartnerOrgsLoadedStatus;
      const destroy$: Subject<boolean> = new Subject<boolean>();

      return new Promise(resolve => {
        Store.select(partnerOrgsLoaded)
          .pipe(takeUntil(destroy$))
          .subscribe(loaded => {
            if (!loaded) {
              Store.dispatch(new LoadPartnerOrgs(payload));
            } else {
              const partnerOrgs = getPartnerOrgsEntitiesList;
              const partnerOrgs$ = Store.select(partnerOrgs);
              destroy$.next(true);
              destroy$.unsubscribe();
              resolve(partnerOrgs$);
            }
          });
      });
    },
  ];

  const isSummerSchoolStudentResolve = [
    'filteredStudents',
    'currentStudentId',
    'currentSchool',
    'ImStudent',
    (filteredStudents: IStudent[], currentStudentId: string, currentSchool: ISchool, ImStudent: ImStudent) => {
      const student = filteredStudents.find(({ _id }) => _id === currentStudentId);
      return ImStudent.isSummerOnly(student, currentSchool._id);
    },
  ];

  let sharedResolve = {
    parsedFilterParam: [
      '$stateParams',
      'ObjectCache',
      'UtilitiesService',
      ($stateParams, ObjectCache, UtilitiesService) => {
        // If the filter param is empty/undefined, return the hash of an empty object instead
        if ($stateParams.filter && UtilitiesService.isHash($stateParams.filter)) {
          return ObjectCache.getObject($stateParams.filter).then(filter => {
            if (_.isUndefined(filter)) {
              throw new Error(`Filter with key ${$stateParams.filter.trim()} not found.`);
            } else {
              return filter;
            }
          });
        } else if ($stateParams.filter) {
          throw new Error(`Invalid filter: '${$stateParams.filter}'`);
        } else {
          return {};
        }
      },
    ],
    parsedSortParam: [
      '$stateParams',
      'ObjectCache',
      'UtilitiesService',
      'SDCStore',
      '$location',
      '$q',
      ($stateParams, ObjectCache, UtilitiesService) => {
        if ($stateParams.sort && UtilitiesService.isHash($stateParams.sort)) {
          return ObjectCache.getObject($stateParams.sort).then(sort => {
            if (_.isUndefined(sort)) {
              throw new Error(`Sort with key ${$stateParams.sort.trim()} not found.`);
            } else {
              return sort;
            }
          });
        } else if ($stateParams.sort) {
          throw new Error(`Invalid sort: '${$stateParams.sort}'`);
        } else {
          return [];
        }
      },
    ],

    // for SSV
    students: [
      'parsedFilterParam',
      'parsedSortParam',
      'currentSchool',
      'StudentSet',
      'SDCStore',
      '$q',
      'UtilitiesService',
      function(parsedFilterParam, parsedSortParam, currentSchool, StudentSet, SDCStore, $q, UtilitiesService) {
        let sortKeys = _.map(parsedSortParam, (c: any) => {
          return c.colId;
        });

        if (cachedStudents === null) {
          let promise;
          const columnKeys = _.union(_.keys(parsedFilterParam), sortKeys, [
            'studentId',
            'studentName',
            'firstName',
            'lastName',
          ]);

          const schoolId = currentSchool._id;
          promise = StudentSet.fetchStudents({
            schoolId,
            columnKeys,
            whereQuery: false,
          }).then(ret => ret.students);

          return promise.then(students => (cachedStudents = students));
        }

        return cachedStudents;
      },
    ],
    // for SSV
    filteredStudents: [
      'students',
      'parsedFilterParam',
      'parsedSortParam',
      'StudentSet',
      'currentSchool',
      function(students, parsedFilterParam, parsedSortParam, StudentSet, currentSchool) {
        let sortKeys = _.map(parsedSortParam, (c: any) => {
          return c.colId;
        });

        if (cachedFilteredStudents === null) {
          const columnKeys = _.union(_.keys(parsedFilterParam), sortKeys, [
            'studentId',
            'studentName',
            'firstName',
            'lastName',
          ]);
          const flattenedStudents = StudentSet.flattenStudents(students, currentSchool, columnKeys);
          cachedFilteredStudents = StudentSet.filterStudents(students, flattenedStudents, parsedFilterParam)
            .flattenedStudents;

          if (parsedSortParam.length > 0) {
            cachedFilteredStudents = StudentSet.sortFlattenedStudents(cachedFilteredStudents, parsedSortParam);
          }
        }

        if (!cachedFilteredStudents || !cachedFilteredStudents.length) {
          const _id = parsedFilterParam._id && parsedFilterParam._id[0];

          // this is needed for the summer school list to work in cases where a student attends another school
          if (_id) {
            cachedFilteredStudents = [{ _id }];
          }
        }

        return cachedFilteredStudents;
      },
    ],
  };

  // state triggered only when user clicks `SHOW ALL` From SSV.
  $stateProvider
    .state('main.school.single-student-filter-all', {
      url: '/student/?filter&sort',
      resolve: sharedResolve,
      controller: [
        '$state',
        '$stateParams',
        'ObjectCache',
        'SDCStore',
        function($state, $stateParams, ObjectCache, SDCStore) {
          $state.go(
            'main.school.single-student-select-first-student',
            _.extend(
              {
                filter: ObjectCache.cacheObject({}),
                sort: ObjectCache.cacheObject(SDCStore.getSort()),
              },
              $stateParams,
            ),
            { location: 'replace' },
          );
        },
      ],
    })

    // location: 'replace' ensures that when you are on the first student,
    // clicking browser back actually returns you to the SDC view
    // without it, just remains on the first student.
    .state('main.school.single-student-select-first-student', {
      url: '/student/?filter&sort',
      resolve: sharedResolve,
      controller: [
        'filteredStudents',
        '$state',
        '$stateParams',
        'SpinnerModal',
        'SDCStore',
        'currentSchool',
        'ImStudent',
        function(
          filteredStudents,
          $state: IStateService,
          $stateParams: IStateParamsService,
          SpinnerModal,
          SDCStore: ISdcStore,
          currentSchool: ISchool,
          ImStudent: ImStudent,
        ) {
          SpinnerModal.activateSpinner('Loading...');
          const studentId: string = filteredStudents.length && filteredStudents[0]._id;
          const currentSdc = SDCStore.getSdcKeyFromUiState();
          const sdcKey = currentSdc || 'default-sdc';

          const isSummerSchoolStudent = ImStudent.isSummerOnly(filteredStudents[0], currentSchool._id);
          const state = !isSummerSchoolStudent
            ? 'main.school.single-student'
            : 'main.school.summer-school-single-student';
          $state.go(
            state,
            {
              studentId,
              filter: $stateParams.filter,
              sort: $stateParams.sort,
              sdcKey,
            },
            {
              location: 'replace',
            },
          );
        },
      ],
    })

    .state('main.school.single-student', {
      params: { sdcKey: { value: null, squash: true } },
      url: '/:sdcKey/student/:studentId/?filter&sort',
      /* MAGIC LOGIC ALERT:
            the parameter in [] should be hyphenated when the name has multiple words in camel case
            eg: docLogs should be [doc-logs] (dvn) */
      template: `
        <student
          [student-list$]="$resolve.studentList$"
          [current-student$]=$resolve.currentStudent$
          [channel]="$resolve.channel"
          [current-school]="$resolve.currentSchool"
          [current-user]="$resolve.currentUser"
          [current-student-id]="$resolve.currentStudentId"
          [student-diffs]="$resolve.studentDiffs"
          [filtered-students]="$resolve.filteredStudents"
          [school-users]="$resolve.schoolUsers"
          [students]="$resolve.students"
          [student-supports$]="$resolve.studentSupports$"
          [supports]="$resolve.supports"
          [parsed-filter-param]="$resolve.parsedFilterParam"
          [parsed-sort-param]="$resolve.parsedSortParam"
          [student-gap-plans]="$resolve.studentGapPlans"
          [flags$]="$resolve.flags$"
          [toggle-version]="$resolve.appVersion"
          [ssv-exit-state]="$ctrl.ssvExitState"
          [doc-logs$]="$resolve.docLogs$"
          [milestones$]="$resolve.milestones$"
          [student-paths$]="$resolve.studentPaths$"
          [college-paths$]="$resolve.collegePaths$"
          [career-paths$]="$resolve.careerPaths$"
          [activities$]="$resolve.activities$"
          [student-activities$]="$resolve.studentActivities$"
          [partner-orgs$]="$resolve.partnerOrgsResolve$"
          [patch-processing$]="$resolve.patchProcessing$"
          [student-assessments$]="$resolve.studentAssessments$"
          [is-summer-school-student]="$resolve.isSummerSchoolStudent"
        >
        </student>`,
      resolve: _.merge(
        {
          currentStudentId: ['$stateParams', $stateParams => $stateParams.studentId],
          currentStudent$: singleStudentViewCurrentStudentResolver,
          studentList$: studentListResolver,
          patchProcessing$: patchProcessingResolver,
          schoolUsers: schoolUsersResolve,
          studentSupports$: studentSupportsResolve,
          supports: supportsResolve,
          studentGapPlans: studentGapPlansResolve,
          flags$: flagsResolve$,
          activities$: activitiesResolve$,
          studentActivities$: studentActivitiesResolve$,
          partnerOrgs$: partnerOrgsResolve$,
          studentDiffs: studentDiffsResolve,
          docLogs$: docLogsResolve,
          milestones$: milestoneResolve,
          studentPaths$: studentPathsResolve,
          collegePaths$: collegePathsResolve,
          careerPaths$: careerPathsResolve,
          studentAssessments$: studentAssessmentsResolve$,
          isSummerSchoolStudent: isSummerSchoolStudentResolve,
        },
        sharedResolve,
      ),
      onEnter(SpinnerModal, LocalStorageService: LocalStorageService) {
        // local storage ssv panel mismatches can happen between v2 and v3, which
        // can cause errors in the single student view
        // this guards against those mismatches
        // TODO: remove once v2 is deprecated (CM).
        function guardAgainstSsvPanelMismatches(config) {
          const ssvPanelsLocalStorageKeys: string[] = ['ssvCollapsedPanels', 'ssvOpenPanels'];
          const configPanelIds = _.map(config, (value: any) => value.id);

          _.each(ssvPanelsLocalStorageKeys, localStorageKey => {
            const localStoragePanels = LocalStorageService.getItem(localStorageKey);
            const misMatchedPanels = _.reduce(
              localStoragePanels,
              (acc, panel) => {
                if (!_.includes(configPanelIds, panel)) {
                  acc.push(panel);
                }

                return acc;
              },
              [],
            );

            if (misMatchedPanels.length) {
              const panelsToStore = _.difference(localStoragePanels, misMatchedPanels);

              LocalStorageService.setItem(localStorageKey, panelsToStore);
            }
          });
        }

        guardAgainstSsvPanelMismatches(StudentPanelsConfig);

        SpinnerModal.deactivateSpinner();
      },
      onExit($state) {
        // CLEARS CACHE
        $state.transition.then(toState => {
          if (toState.name !== 'main.school.single-student') {
            cachedSchoolUsers = cachedStudents = cachedFilteredStudents = null;
          }
        });
      },
      ncyBreadcrumb: {
        skip: true,
      },
    })
    .state('main.school.summer-school-single-student', {
      params: { sdcKey: { value: null, squash: true } },
      url: '/:sdcKey/student/:studentId/?filter&sort',
      /* MAGIC LOGIC ALERT:
            the parameter in [] should be hyphenated when the name has multiple words in camel case
            eg: docLogs should be [doc-logs] (dvn) */
      template: `
        <student
          [student-list$]="$resolve.studentList$"
          [current-student$]=$resolve.currentStudent$
          [channel]="$resolve.channel"
          [current-school]="$resolve.currentSchool"
          [current-user]="$resolve.currentUser"
          [current-student-id]="$resolve.currentStudentId"
          [filtered-students]="$resolve.filteredStudents"
          [school-users]="$resolve.schoolUsers"
          [students]="$resolve.students"
          [parsed-filter-param]="$resolve.parsedFilterParam"
          [parsed-sort-param]="$resolve.parsedSortParam"
          [student-gap-plans]="$resolve.studentGapPlans"
          [toggle-version]="$resolve.appVersion"
          [ssv-exit-state]="$ctrl.ssvExitState"
          [patch-processing$]="$resolve.patchProcessing$"
          [is-summer-school-student]="$resolve.isSummerSchoolStudent"
        >
        </student>`,
      resolve: _.merge(
        {
          currentStudentId: ['$stateParams', $stateParams => $stateParams.studentId],
          currentStudent$: singleStudentViewCurrentStudentResolver,
          studentList$: studentListResolver,
          patchProcessing$: patchProcessingResolver,
          schoolUsers: schoolUsersResolve,
          supports: supportsResolve,
          studentGapPlans: studentGapPlansResolve,
          studentPaths$: studentPathsResolve,
          isSummerSchoolStudent: isSummerSchoolStudentResolve,
        },
        sharedResolve,
      ),
      onEnter(SpinnerModal) {
        SpinnerModal.deactivateSpinner();
      },
      onExit($state) {
        // CLEARS CACHE
        $state.transition.then(toState => {
          if (toState.name !== 'main.school.summer-school-single-student') {
            cachedSchoolUsers = cachedStudents = cachedFilteredStudents = null;
          }
        });
      },
      ncyBreadcrumb: {
        skip: true,
      },
    });
});
